import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Minus, Calendar } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip, Legend } from 'recharts';
import adminService from '../services/adminService';
import dashboardService from '../services/dashboardService';

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Admin Home';
  }, []);

  // State for data
  const [tasks, setTasks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => {
    // Initialize to current date - weekends now allowed
    const today = new Date();
    
    // Removed weekend skip logic - bookings now available 7 days a week
    // if (dayOfWeek === 0) { // Sunday
    //   today.setDate(today.getDate() + 1); // Move to Monday
    // } else if (dayOfWeek === 6) { // Saturday  
    //   today.setDate(today.getDate() + 2); // Move to Monday
    // }
    
    console.log('📅 Admin Dashboard: Initial date set to:', today.toDateString(), '(Day of week:', today.getDay(), ')');
    return today;
  });
  const [expandedAppointments, setExpandedAppointments] = useState(new Set());
  
  // State for admin info
  const [adminId, setAdminId] = useState(null);
  
  // State for dashboard statistics
  const [dashboardStats, setDashboardStats] = useState({
    total_bookings: 0,
    clinical_telehealth_bookings: 0,
    general_telehealth_bookings: 0,
    total_users: 0,
    new_users_15_days: 0,
    new_doctors_15_days: 0,
    new_clinicians_15_days: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);
  
  // State for chart time periods
  const [clinicalChartPeriod, setClinicalChartPeriod] = useState('12months'); // '12months' or '30days'
  const [generalChartPeriod, setGeneralChartPeriod] = useState('12months'); // '12months' or '30days'
  const [comparisonChartPeriod, setComparisonChartPeriod] = useState('12months'); // '12months' or '30days'
  
  // State for chart data
  const [monthlyChartData, setMonthlyChartData] = useState([]);
  const [dailyChartData, setDailyChartData] = useState([]);
  const [chartDataLoading, setChartDataLoading] = useState(true);

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
    fetchAdminInfo();
    fetchDashboardStats();
    fetchChartData();
  }, []);

  // Fetch appointments when selected date or admin changes
  useEffect(() => {
    if (adminId && selectedDate) {
      fetchAppointmentsForDate(selectedDate);
    }
  }, [adminId, selectedDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tasksData, notificationsData, newsData] = await Promise.all([
        adminService.getTasks(),
        adminService.getNotifications(),
        adminService.getLatestNews()
      ]);

      setTasks(tasksData);
      setNotifications(notificationsData);
      setNews(newsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Handle authentication errors
      if (error.message.includes('Could not validate credentials') || error.message.includes('401')) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/auth/login';
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminInfo = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      console.log('👤 Admin Dashboard: Current user from localStorage:', user);
      
      if (user.id) {
        const admin = await adminService.getAdminByUserId(user.id);
        console.log('👨‍💼 Admin Dashboard: Admin info retrieved:', admin);
        setAdminId(admin.id);
        console.log('🆔 Admin Dashboard: Set admin ID to:', admin.id);
      } else {
        console.log('❌ Admin Dashboard: No user ID found in localStorage');
      }
    } catch (error) {
      console.error('❌ Admin Dashboard: Error fetching admin info:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      setStatsLoading(true);
      console.log('📊 Admin Dashboard: Fetching dashboard statistics');
      
      const stats = await dashboardService.getDashboardStats();
      console.log('✅ Admin Dashboard: Dashboard stats retrieved:', stats);
      
      setDashboardStats(stats);
    } catch (error) {
      console.error('❌ Admin Dashboard: Error fetching dashboard stats:', error);
      
      // Handle authentication errors
      if (error.message.includes('Could not validate credentials') || error.message.includes('401')) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/auth/login';
        return;
      }
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      setChartDataLoading(true);
      console.log('📊 Admin Dashboard: Fetching chart data');
      
      const [monthly, daily] = await Promise.all([
        dashboardService.getMonthlyChartData(),
        dashboardService.getDailyChartData()
      ]);
      
      console.log('✅ Admin Dashboard: Chart data retrieved');
      setMonthlyChartData(monthly);
      setDailyChartData(daily);
    } catch (error) {
      console.error('❌ Admin Dashboard: Error fetching chart data:', error);
    } finally {
      setChartDataLoading(false);
    }
  };

  const fetchAppointmentsForDate = async (date) => {
    try {
      // Format date as YYYY-MM-DD without timezone conversion
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      console.log('🔍 Admin Dashboard: Fetching system activities for admin:', adminId, 'date:', dateString, 'original date:', date);
      
      let appointmentsData = await adminService.getAdminBookings(adminId, dateString);
      console.log('📅 Admin Dashboard: Raw system activities data from API:', appointmentsData);
      console.log('📊 Admin Dashboard: Number of real activities:', appointmentsData.length);
      
      // Add some demo system activities to demonstrate the feature
      const dummyActivities = generateDummyActivities(date, appointmentsData.length);
      console.log('🎭 Admin Dashboard: Generated demo activities:', dummyActivities.length);
      
      appointmentsData = [...appointmentsData, ...dummyActivities];
      console.log('📋 Admin Dashboard: Total activities to display:', appointmentsData.length, '(Real:', appointmentsData.length - dummyActivities.length, 'Demo:', dummyActivities.length, ')');
      
      // Sort all activities by time
      appointmentsData.sort((a, b) => {
        const timeA = a.activity_time || a.time;
        const timeB = b.activity_time || b.time;
        return timeA.localeCompare(timeB);
      });
      console.log('📅 Admin Dashboard: Activities sorted by time:', appointmentsData.map(act => ({ time: act.activity_time || act.time, description: act.description || act.activityDescription })));
      
      setAppointments(appointmentsData);
    } catch (error) {
      console.error('❌ Admin Dashboard: Error fetching activities for date:', error);
      setAppointments([]);
    }
  };

  // Generate dummy system activities for demonstration
  const generateDummyActivities = (date, existingCount) => {
    const systemActivities = [
      { description: 'System Backup Scheduled', type: 'System Maintenance', priority: 'High' },
      { description: 'User Registration - Dr. Smith', type: 'User Management', priority: 'Medium' },
      { description: 'Database Optimization', type: 'System Maintenance', priority: 'Low' },
      { description: 'Security Audit Review', type: 'Security', priority: 'High' },
      { description: 'Performance Monitoring Check', type: 'Monitoring', priority: 'Medium' },
      { description: 'Email Server Maintenance', type: 'System Maintenance', priority: 'Medium' },
      { description: 'New Clinic Registration Review', type: 'Business', priority: 'High' },
      { description: 'Patient Data Backup', type: 'Data Management', priority: 'High' },
      { description: 'System Log Analysis', type: 'Monitoring', priority: 'Low' },
      { description: 'Software Update Deployment', type: 'System Update', priority: 'Medium' },
      { description: 'User Access Review', type: 'Security', priority: 'High' },
      { description: 'Database Health Check', type: 'Monitoring', priority: 'Medium' },
      { description: 'Report Generation Task', type: 'Analytics', priority: 'Low' },
      { description: 'Network Security Scan', type: 'Security', priority: 'High' },
      { description: 'Backup Verification Process', type: 'Data Management', priority: 'Medium' }
    ];
    
    // Ensure at least 20 total activities (real + dummy)
    const targetTotal = 20;
    const dummyCount = Math.max(0, targetTotal - existingCount);
    const dummy = [];
    
    for (let i = 0; i < dummyCount; i++) {
      // Generate activities throughout the day (8 AM to 6 PM)
      const baseHour = 8 + Math.floor(i / 2); // Every 30 minutes starting from 8 AM
      const minute = (i % 2) * 30; // Alternate between :00 and :30
      const hour = baseHour > 18 ? 8 + (i % 22) : baseHour; // Wrap around if we exceed 6 PM
      
      const selectedActivity = systemActivities[Math.floor(Math.random() * systemActivities.length)];
      
      dummy.push({
        id: `dummy-admin-${date.toDateString()}-${i}`,
        time: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`,
        activity_time: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`,
        date: date.toISOString().split('T')[0],
        activityDescription: selectedActivity.description,
        description: selectedActivity.description,
        activity_type: selectedActivity.type,
        priority: selectedActivity.priority,
        status: 'Scheduled',
        isDummy: true // Flag to identify dummy activities
      });
    }
    
    return dummy;
  };

  const handleTaskToggle = async (taskId) => {
    try {
      const updatedTasks = tasks.map(task => 
        task.id === taskId ? { ...task, checked: !task.checked } : task
      );
      setTasks(updatedTasks);
      
      // Update in backend
      const task = tasks.find(t => t.id === taskId);
      await adminService.updateTaskStatus(taskId, !task.checked);
    } catch (error) {
      console.error('Error updating task:', error);
      // Revert on error
      fetchData();
    }
  };

  const handleNotificationToggle = async (notificationId) => {
    try {
      const updatedNotifications = notifications.map(notification => 
        notification.id === notificationId ? { ...notification, checked: !notification.checked } : notification
      );
      setNotifications(updatedNotifications);
      
      // Update in backend
      const notification = notifications.find(n => n.id === notificationId);
      await adminService.updateNotificationStatus(notificationId, !notification.checked);
    } catch (error) {
      console.error('Error updating notification:', error);
      // Revert on error
      fetchData();
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Reset time to compare only dates
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    yesterday.setHours(0, 0, 0, 0);
    tomorrow.setHours(0, 0, 0, 0);
    
    if (compareDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (compareDate.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    } else if (compareDate.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
    }
  };

  const handleDateChange = (direction) => {
    setSelectedDate(prevDate => {
      const newDate = new Date(prevDate);
      
      if (direction === 'next') {
        // Move to next day - weekends now allowed
        newDate.setDate(newDate.getDate() + 1);
      } else {
        // Move to previous day - weekends now allowed
        newDate.setDate(newDate.getDate() - 1);
      }
      
      console.log('📅 Admin Dashboard: Date changed to:', newDate.toDateString(), '(Day of week:', newDate.getDay(), ')');
      return newDate;
    });
    // Clear expanded activities when date changes
    setExpandedAppointments(new Set());
  };

  const handleAppointmentToggle = (appointmentId) => {
    setExpandedAppointments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(appointmentId)) {
        newSet.delete(appointmentId);
      } else {
        newSet.add(appointmentId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-2">
            <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-gray-600">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // Get chart data based on selected period - now using real data from API
  const getClinicalChartData = () => {
    return clinicalChartPeriod === '12months' ? monthlyChartData : dailyChartData;
  };

  const getGeneralChartData = () => {
    return generalChartPeriod === '12months' ? monthlyChartData : dailyChartData;
  };

  const getComparisonChartData = () => {
    return comparisonChartPeriod === '12months' ? monthlyChartData : dailyChartData;
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="flex gap-6">
        {/* Left Column */}
        <div className="w-[22%] space-y-2">
          {/* Tasks Section */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="bg-blue-600 text-white px-3 py-1 rounded-t-lg text-center">
              <h3 className="font-semibold text-sm">Tasks</h3>
            </div>
            <div className="p-2 space-y-2">
              {tasks.map((task) => (
                <div key={task.id} className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    checked={task.checked}
                    onChange={() => handleTaskToggle(task.id)}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-700">{task.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Notifications Section */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="bg-blue-600 text-white px-3 py-1 rounded-t-lg text-center">
              <h3 className="font-semibold text-sm">Notifications</h3>
            </div>
            <div className="p-2 space-y-2">
              {notifications.map((notification) => (
                <div key={notification.id} className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    checked={notification.checked}
                    onChange={() => handleNotificationToggle(notification.id)}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-700">{notification.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Latest News Section */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="bg-blue-600 text-white px-3 py-1 rounded-t-lg text-center">
              <h3 className="font-semibold text-sm">Latest News</h3>
            </div>
            <div className="p-2 space-y-2">
              {news.map((item) => (
                <div key={item.id} className="flex space-x-3">
                  <div className="w-16 h-16 bg-gray-300 rounded flex-shrink-0"></div>
                  <div className="flex-1">
                    <h4 className="font-medium text-sm">{item.title}</h4>
                    <p className="text-xs text-gray-600 mt-1">{item.excerpt}</p>
                    <a href="#" className="text-blue-600 hover:text-blue-800 text-xs">
                      + Read More
                    </a>
                  </div>
                </div>
              ))}
              <div className="flex justify-end mt-2">
                <button className="bg-white text-blue-600 border border-blue-600 px-3 py-1 rounded text-xs hover:bg-blue-600 hover:text-white transition-colors">
                  News Page
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="w-[78%] space-y-4">
          {/* Quick Access Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="bg-blue-600 text-white px-4 py-2 rounded-t-lg">
              <h3 className="font-semibold text-sm text-center">Quick Access</h3>
            </div>
            <div className="p-4">
              <div className="flex justify-center space-x-3">
                <Link to="/admin/booking" className="bg-blue-100 text-gray-700 px-4 py-2 rounded text-sm hover:bg-blue-200 transition-colors font-medium">
                  Make A Booking
                </Link>
                <Link to="/admin/patientfiles" className="bg-blue-100 text-gray-700 px-4 py-2 rounded text-sm hover:bg-blue-200 transition-colors font-medium">
                  Patients
                </Link>
                <button className="bg-blue-100 text-gray-700 px-4 py-2 rounded text-sm hover:bg-blue-200 transition-colors font-medium">
                  Reports
                </button>
                <Link to="/admin/clinics" className="bg-blue-100 text-gray-700 px-4 py-2 rounded text-sm hover:bg-blue-200 transition-colors font-medium">
                  Clinics
                </Link>
                <Link to="/admin/doctors" className="bg-blue-100 text-gray-700 px-4 py-2 rounded text-sm hover:bg-blue-200 transition-colors font-medium">
                  Doctors
                </Link>
                <Link to="/admin/clinicians" className="bg-blue-100 text-gray-700 px-4 py-2 rounded text-sm hover:bg-blue-200 transition-colors font-medium">
                  Clinicians
                </Link>
                <Link to="/admin/users" className="bg-blue-100 text-gray-700 px-4 py-2 rounded text-sm hover:bg-blue-200 transition-colors font-medium">
                  Users
                </Link>
                <Link to="/admin/connex-monitor" className="bg-green-100 text-gray-700 px-4 py-2 rounded text-sm hover:bg-green-200 transition-colors font-medium">
                  Connex Monitor
                </Link>
                <Link to="/admin/ac-vitals-monitor" className="bg-purple-100 text-gray-700 px-4 py-2 rounded text-sm hover:bg-purple-200 transition-colors font-medium">
                  AC Vitals Monitor
                </Link>
              </div>
            </div>
          </div>

          {/* Metrics Cards Grid */}
          <div className="grid grid-cols-4 gap-4">
            {/* Row 1 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-center">
                {statsLoading ? (
                  <div className="flex items-center justify-center py-2">
                    <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : (
                  <div className="text-3xl font-bold text-blue-600 mb-1">{dashboardStats.total_bookings.toLocaleString()}</div>
                )}
                <div className="text-sm text-gray-600 mb-1">Total Bookings</div>
                <div className="text-xs text-blue-600">All Time</div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-center">
                {statsLoading ? (
                  <div className="flex items-center justify-center py-2">
                    <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : (
                  <div className="text-3xl font-bold text-blue-600 mb-1">{dashboardStats.clinical_telehealth_bookings.toLocaleString()}</div>
                )}
                <div className="text-sm text-gray-600 mb-1">Clinical Tele-Health</div>
                <div className="text-xs text-blue-600">All Time</div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-center">
                {statsLoading ? (
                  <div className="flex items-center justify-center py-2">
                    <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : (
                  <div className="text-3xl font-bold text-blue-600 mb-1">{dashboardStats.general_telehealth_bookings.toLocaleString()}</div>
                )}
                <div className="text-sm text-gray-600 mb-1">General Tele-Health</div>
                <div className="text-xs text-blue-600">All Time</div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-center">
                {statsLoading ? (
                  <div className="flex items-center justify-center py-2">
                    <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : (
                  <div className="text-3xl font-bold text-blue-600 mb-1">
                    {dashboardStats.total_bookings > 0 
                      ? `${Math.round(((dashboardStats.clinical_telehealth_bookings + dashboardStats.general_telehealth_bookings) / dashboardStats.total_bookings) * 100)}%`
                      : '0%'
                    }
                  </div>
                )}
                <div className="text-sm text-gray-600 mb-1">Telehealth Utilisation</div>
                <div className="text-xs text-blue-600">All Time</div>
              </div>
            </div>

            {/* Row 2 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-center">
                {statsLoading ? (
                  <div className="flex items-center justify-center py-2">
                    <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : (
                  <div className="text-3xl font-bold text-blue-600 mb-1">{dashboardStats.total_users.toLocaleString()}</div>
                )}
                <div className="text-sm text-gray-600 mb-1">Total Users</div>
                <div className="text-xs text-blue-600">All Time</div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-center">
                {statsLoading ? (
                  <div className="flex items-center justify-center py-2">
                    <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 0 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : (
                  <div className="text-3xl font-bold text-blue-600 mb-1">{dashboardStats.new_users_15_days.toLocaleString()}</div>
                )}
                <div className="text-sm text-gray-600 mb-1">New Users</div>
                <div className="text-xs text-blue-600">Last 15 Days</div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-center">
                {statsLoading ? (
                  <div className="flex items-center justify-center py-2">
                    <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : (
                  <div className="text-3xl font-bold text-blue-600 mb-1">{dashboardStats.new_doctors_15_days.toLocaleString()}</div>
                )}
                <div className="text-sm text-gray-600 mb-1">New Doctors</div>
                <div className="text-xs text-blue-600">Last 15 Days</div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-center">
                {statsLoading ? (
                  <div className="flex items-center justify-center py-2">
                    <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 0 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : (
                  <div className="text-3xl font-bold text-blue-600 mb-1">{dashboardStats.new_clinicians_15_days.toLocaleString()}</div>
                )}
                <div className="text-sm text-gray-600 mb-1">New Clinicians</div>
                <div className="text-xs text-blue-600">Last 15 Days</div>
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-2 gap-4">
            {/* Clinical Tele-Health Bookings Chart */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Clinical Tele-Health Bookings</h3>
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setClinicalChartPeriod('12months')}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      clinicalChartPeriod === '12months'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    12 Months
                  </button>
                  <button
                    onClick={() => setClinicalChartPeriod('30days')}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      clinicalChartPeriod === '30days'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    30 Days
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-3 text-center">
                {clinicalChartPeriod === '12months' ? 'Monthly bookings for the past 12 months' : 'Daily bookings for the past 30 days'}
              </p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={getClinicalChartData()}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="period" 
                      stroke="#6B7280"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis 
                      stroke="#6B7280"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '12px'
                      }}
                      labelStyle={{ color: '#9CA3AF' }}
                    />
                    <Legend 
                      wrapperStyle={{ fontSize: '12px' }}
                    />
                    <Bar 
                      dataKey="clinical" 
                      fill="#3B82F6" 
                      name="Clinical Bookings"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* General Tele-Health Bookings Chart */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">General Tele-Health Bookings</h3>
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setGeneralChartPeriod('12months')}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      generalChartPeriod === '12months'
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    12 Months
                  </button>
                  <button
                    onClick={() => setGeneralChartPeriod('30days')}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      generalChartPeriod === '30days'
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    30 Days
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-3 text-center">
                {generalChartPeriod === '12months' ? 'Monthly bookings for the past 12 months' : 'Daily bookings for the past 30 days'}
              </p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={getGeneralChartData()}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="period" 
                      stroke="#6B7280"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis 
                      stroke="#6B7280"
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '12px'
                      }}
                      labelStyle={{ color: '#9CA3AF' }}
                    />
                    <Legend 
                      wrapperStyle={{ fontSize: '12px' }}
                    />
                    <Bar 
                      dataKey="general" 
                      fill="#10B981" 
                      name="General Bookings"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Combined Comparison Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Clinical vs General Tele-Health Bookings Comparison</h3>
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setComparisonChartPeriod('12months')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    comparisonChartPeriod === '12months'
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  12 Months
                </button>
                <button
                  onClick={() => setComparisonChartPeriod('30days')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    comparisonChartPeriod === '30days'
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  30 Days
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-3 text-center">
              {comparisonChartPeriod === '12months' ? 'Monthly comparison of booking types for the past 12 months' : 'Daily comparison of booking types for the past 30 days'}
            </p>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={getComparisonChartData()}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="period" 
                    stroke="#6B7280"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    stroke="#6B7280"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '12px'
                    }}
                    labelStyle={{ color: '#9CA3AF' }}
                  />
                  <Legend 
                    wrapperStyle={{ fontSize: '12px' }}
                  />
                  <Bar 
                    dataKey="clinical" 
                    fill="#3B82F6" 
                    name="Clinical Bookings"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar 
                    dataKey="general" 
                    fill="#10B981" 
                    name="General Bookings"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;

